import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Button, Form, InputGroup, Pagination } from 'react-bootstrap';
import { Link, useNavigate, useNavigationType, useLocation, useSearchParams } from 'react-router-dom';
import { companyService, userService } from '../services/apiService';
import { usePermissions } from '../context/PermissionsContext';
import { useAuthToken } from '../hooks/useAuthToken';
import { useIsMobile } from '../hooks/useMediaQuery';
import SortableHeader from '../components/SortableHeader';
import { LoadingState, ErrorState, serverMessage } from '../components/PageState';
import { planLabel } from '../components/PlanFilter';
import { formatDate, formatNumber, EMPTY } from '../utils/format';
import { rowClick } from '../utils/rows';

const PAGE_SIZE = 50;

// List state lives in the URL, so Back from a company returns to the same search, sort, filters and page.
// Values equal to these defaults are left out of the URL.
const DEFAULTS = { q: '', sort: 'totalUsers', dir: 'desc', payment: 'all', cycle: 'all', plan: 'all', page: '1' };
const SORT_FIELDS = ['name', 'freeUsers', 'paidUsers', 'totalUsers', 'renewalDate'];
const NUMBER_FIELDS = ['freeUsers', 'paidUsers', 'totalUsers'];
const PAYMENT_OPTIONS = ['all', 'stripe', 'manual'];
const CYCLE_OPTIONS = ['all', 'monthly', 'annual'];
const PLAN_OPTIONS = ['FREE_TRIAL', 'STANDARD', 'PROFESSIONAL', 'PREMIUM', 'ENTERPRISE'];

const oneOf = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

// The value a search result sorts by: numbers as numbers, dates as times, text as text. Missing
// values come back as null so they can sort last in both directions.
const sortValue = (company, field) => {
  const value = company[field];
  if (value === null || value === undefined || value === '') return null;
  if (field === 'renewalDate') {
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
  }
  if (NUMBER_FIELDS.includes(field)) {
    const number = Number(value);
    return Number.isNaN(number) ? null : number;
  }
  return String(value);
};

const compareCompanies = (field, direction) => (a, b) => {
  const aValue = sortValue(a, field);
  const bValue = sortValue(b, field);
  if (aValue === null && bValue === null) return 0;
  if (aValue === null) return 1;
  if (bValue === null) return -1;
  const order = typeof aValue === 'string'
    ? aValue.localeCompare(bValue, 'en-GB', { sensitivity: 'base' })
    : aValue - bValue;
  return direction === 'asc' ? order : -order;
};

const Companies = () => {
  const { canEdit } = usePermissions();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const location = useLocation();
  const ensureToken = useAuthToken();
  // The Companies view is mounted on both /companies and /crm. On /crm we hide the Join company action.
  const isCrmView = location.pathname === '/crm';
  const [searchParams, setSearchParams] = useSearchParams();

  const sortField = oneOf(searchParams.get('sort'), SORT_FIELDS, DEFAULTS.sort);
  const sortDirection = oneOf(searchParams.get('dir'), ['asc', 'desc'], DEFAULTS.dir);
  const paymentMethod = oneOf(searchParams.get('payment'), PAYMENT_OPTIONS, DEFAULTS.payment);
  const billingCycle = oneOf(searchParams.get('cycle'), CYCLE_OPTIONS, DEFAULTS.cycle);
  const tier = oneOf(searchParams.get('plan'), PLAN_OPTIONS, DEFAULTS.plan);
  const currentPage = Math.max(0, (parseInt(searchParams.get('page'), 10) || 1) - 1);

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [joiningCompanyId, setJoiningCompanyId] = useState(null);
  // The outcome of the last Join company click, shown under that row's button
  const [joinResult, setJoinResult] = useState(null);
  // The box keeps its own state so typing stays responsive; ?q= follows it. A search handed over from the home page arrives as ?q=
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  // Only the newest request may update the list, so a slow older reply never replaces newer results
  const requestId = useRef(0);
  const firstSearch = useRef(true);

  // Follow ?q= when it changes from outside the box, as when the Companies link is followed from a search
  // or Back is pressed. The box's own writes replace the entry, and arrive a moment after the keystroke.
  const urlQuery = searchParams.get('q') || '';
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType !== 'REPLACE') setSearchTerm(urlQuery);
  }, [urlQuery, navigationType]);

  const query = searchTerm.trim();
  const isSearching = query !== '';
  const filtersActive = paymentMethod !== 'all' || billingCycle !== 'all' || tier !== 'all';

  const updateParams = useCallback((changes) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === null || value === undefined || String(value) === DEFAULTS[key]) next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const fetchPaginatedCompanies = useCallback(async (page, field, direction, filters) => {
    const id = ++requestId.current;
    try {
      setLoading(true);
      setError(null);
      await ensureToken();
      const response = await companyService.getCompaniesPaginated(page, PAGE_SIZE, field, direction, filters);
      if (id !== requestId.current) return;
      setCompanies(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (err) {
      if (id !== requestId.current) return;
      console.error('Failed to load companies:', err);
      setCompanies([]);
      setError(serverMessage(err, 'Could not load companies.'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [ensureToken]);

  const searchCompanies = useCallback(async (term) => {
    const id = ++requestId.current;
    try {
      setLoading(true);
      setError(null);
      await ensureToken();
      const response = await companyService.searchCompanies(term);
      if (id !== requestId.current) return;
      setCompanies(response.data);
    } catch (err) {
      if (id !== requestId.current) return;
      console.error('Failed to search companies:', err);
      setCompanies([]);
      setError(serverMessage(err, 'Could not search companies.'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [ensureToken]);

  const loadPage = useCallback(() => {
    fetchPaginatedCompanies(currentPage, sortField, sortDirection, { paymentMethod, billingCycle, tier });
  }, [fetchPaginatedCompanies, currentPage, sortField, sortDirection, paymentMethod, billingCycle, tier]);

  // Load paginated companies when not searching (re-fetches on page, sort or filter change)
  useEffect(() => {
    if (!isSearching) loadPage();
  }, [isSearching, loadPage]);

  // Search as the user types, after a pause; a search in the URL on arrival runs straight away
  useEffect(() => {
    if (!isSearching) return undefined;
    const delay = firstSearch.current ? 0 : 400;
    firstSearch.current = false;
    const timer = setTimeout(() => searchCompanies(query), delay);
    return () => clearTimeout(timer);
  }, [isSearching, query, searchCompanies]);

  const retry = () => (isSearching ? searchCompanies(query) : loadPage());

  // Start from the first page whenever a filter changes, so the user sees the start of the filtered set
  const handleFilterChange = (key) => (e) => updateParams({ [key]: e.target.value, page: null });

  const clearFilters = () => updateParams({ payment: null, cycle: null, plan: null, page: null });

  const handleSearchChange = (e) => {
    const value = e.target.value;
    firstSearch.current = false;
    setSearchTerm(value);
    // Clearing the search goes back to the first page of the full list
    updateParams({ q: value || null, page: null });
  };

  const handleSort = (field) => {
    const direction = sortField === field ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    // In paginated mode, jump back to the first page so the user sees the top of the new order
    updateParams({ sort: field, dir: direction, page: null });
  };

  const matchesFilters = (company) => {
    if (paymentMethod === 'stripe' && company.usesStripe !== true) return false;
    if (paymentMethod === 'manual' && company.usesStripe !== false) return false;

    const frequency = (company.paymentFrequency || '').toUpperCase();
    if (billingCycle === 'monthly' && frequency !== 'MONTHLY') return false;
    if (billingCycle === 'annual' && !['YEARLY', 'ANNUAL', 'ANNUALLY'].includes(frequency)) return false;

    if (tier !== 'all' && company.accountType !== tier) return false;

    return true;
  };

  const getSortedCompanies = () => {
    // In paginated mode the server already sorted and filtered the full collection, so don't re-process the current page.
    if (!isSearching) {
      return companies;
    }
    return companies.filter(matchesFilters).sort(compareCompanies(sortField, sortDirection));
  };

  // One click, no confirmation: staff move their own Team Today account into the customer's company
  const handleJoinCompany = async (company) => {
    try {
      setJoiningCompanyId(company.id);
      setJoinResult(null);
      await ensureToken();
      await userService.joinCompany(company.id);
      setJoinResult({ companyId: company.id, ok: true, message: 'Joined' });
      // The user counts change, so reload the list rather than the whole page
      retry();
    } catch (err) {
      console.error('Failed to join company:', err);
      setJoinResult({ companyId: company.id, ok: false, message: 'Could not join. Try again.' });
    } finally {
      setJoiningCompanyId(null);
    }
  };

  const handlePageChange = (page) => {
    updateParams({ page: page + 1 });
  };

  const formatBillingCycle = (frequency) => {
    if (!frequency) return EMPTY;
    const value = frequency.toUpperCase();
    if (value === 'YEARLY' || value === 'ANNUAL' || value === 'ANNUALLY') return 'Annually';
    if (value === 'MONTHLY') return 'Monthly';
    return frequency;
  };

  const companyPath = (company) => (isCrmView ? `/crm/companies/${company.id}` : `/companies/${company.id}`);

  const header = (field, label, className = '', align = '') => (
    <SortableHeader
      field={field}
      sortField={sortField}
      sortDirection={sortDirection}
      onSort={handleSort}
      align={align}
      className={className}
    >
      {label}
    </SortableHeader>
  );

  const renderPagination = () => {
    if (isSearching || totalPages <= 1) return null;

    const displayPage = currentPage + 1;
    // Pages either side of the current one; a phone has room for one
    const neighbours = isMobile ? 1 : 2;

    return (
      <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <small className="text-muted">
          Showing {formatNumber(currentPage * PAGE_SIZE + 1)} to {formatNumber(Math.min((currentPage + 1) * PAGE_SIZE, totalElements))} of {formatNumber(totalElements)} companies
        </small>
        <Pagination className="mb-0">
          <Pagination.Prev
            disabled={currentPage === 0}
            onClick={() => handlePageChange(currentPage - 1)}
          />
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(page => page === 1 || page === totalPages || Math.abs(page - displayPage) <= neighbours)
            .reduce((items, page, idx, arr) => {
              if (idx > 0 && page - arr[idx - 1] > 1) {
                items.push(<Pagination.Ellipsis key={`ellipsis-${page}`} disabled />);
              }
              items.push(
                <Pagination.Item
                  key={page}
                  active={page === displayPage}
                  onClick={() => handlePageChange(page - 1)}
                >
                  {page}
                </Pagination.Item>
              );
              return items;
            }, [])}
          <Pagination.Next
            disabled={currentPage >= totalPages - 1}
            onClick={() => handlePageChange(currentPage + 1)}
          />
        </Pagination>
      </Card.Footer>
    );
  };

  const renderEmpty = () => (
    <div className="p-4 text-center text-muted">
      {isSearching ? `No companies found matching "${query}"` : 'No companies found'}
      {filtersActive && (
        <>
          {' with these filters. '}
          <Button variant="link" size="sm" className="p-0 align-baseline" onClick={clearFilters}>Clear filters</Button>
        </>
      )}
    </div>
  );

  // The paginated list only holds companies paying for at least one seat; a search covers every company
  const description = isCrmView
    ? (isSearching
      ? 'Every company whose name matches, paying or not.'
      : `Paying customers with their plan, billing contact and renewal date${totalElements ? ` (${formatNumber(totalElements)})` : ''}. Open a company for its notes and billing.`)
    : (isSearching
      ? 'Every company whose name matches, paying or not.'
      : `Companies paying for at least one seat${totalElements ? ` (${formatNumber(totalElements)})` : ''}. Search by name to find any company.`);

  const sortedCompanies = getSortedCompanies();

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>{isCrmView ? 'CRM' : 'Companies'}</h1>
          <p className="text-muted">{description}</p>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <div className="d-flex flex-wrap align-items-end gap-3 filter-bar">
                <Form.Group controlId="companies-search" style={{ width: '100%', maxWidth: '400px' }} className="filter-bar-wide">
                  <Form.Label visuallyHidden>Search companies</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="search"
                      placeholder="Search by company name..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      autoFocus
                    />
                    {loading && (
                      <InputGroup.Text>
                        <Spinner animation="border" size="sm" role="status">
                          <span className="visually-hidden">Loading</span>
                        </Spinner>
                      </InputGroup.Text>
                    )}
                  </InputGroup>
                </Form.Group>

                <Form.Group controlId="companies-payment" className="ms-md-auto">
                  <Form.Label className="mb-1 small text-muted">Payment</Form.Label>
                  <Form.Select size="sm" value={paymentMethod} onChange={handleFilterChange('payment')}>
                    <option value="all">All</option>
                    <option value="stripe">Stripe</option>
                    <option value="manual">Manual</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="companies-cycle">
                  <Form.Label className="mb-1 small text-muted">Billing cycle</Form.Label>
                  <Form.Select size="sm" value={billingCycle} onChange={handleFilterChange('cycle')}>
                    <option value="all">All</option>
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annually</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="companies-plan" className="filter-bar-wide">
                  <Form.Label className="mb-1 small text-muted">Plan</Form.Label>
                  <Form.Select size="sm" value={tier} onChange={handleFilterChange('plan')}>
                    <option value="all">All</option>
                    {PLAN_OPTIONS.map((key) => <option key={key} value={key}>{planLabel(key)}</option>)}
                  </Form.Select>
                </Form.Group>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {error ? (
                <ErrorState message={error} onRetry={retry} inCard />
              ) : loading && sortedCompanies.length === 0 ? (
                <LoadingState label={isSearching ? 'Searching' : 'Loading companies'} />
              ) : sortedCompanies.length === 0 ? (
                renderEmpty()
              ) : (
                <Table responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      {header('name', 'Company')}
                      {isCrmView && <th className="d-none d-lg-table-cell">Preferred contact</th>}
                      {header('freeUsers', 'Free seats', 'd-none d-xl-table-cell', 'text-end')}
                      {header('paidUsers', 'Paid seats', 'd-none d-xl-table-cell', 'text-end')}
                      {header('totalUsers', 'Users', '', 'text-end')}
                      <th className="text-center d-none d-lg-table-cell">Payment</th>
                      <th className="text-center d-none d-lg-table-cell">Billing cycle</th>
                      <th className="text-center">Plan</th>
                      {header('renewalDate', 'Renewal date', 'd-none d-md-table-cell', 'text-center')}
                      {!isCrmView && <th className="text-center d-none d-md-table-cell">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCompanies.map((company) => (
                      <tr
                        key={company.id}
                        onClick={rowClick(() => navigate(companyPath(company)))}
                        style={{ cursor: 'pointer' }}
                        className="table-row-hover"
                      >
                        <td className="align-middle">
                          <Link className="cell-link" to={companyPath(company)}>{company.name}</Link>
                        </td>
                        {isCrmView && (
                          <td className="align-middle d-none d-lg-table-cell">
                            {company.preferredPointOfContact || <span className="text-muted">{EMPTY}</span>}
                          </td>
                        )}
                        <td className="text-end align-middle d-none d-xl-table-cell">
                          {formatNumber(company.freeUsers)}
                        </td>
                        <td className="text-end align-middle d-none d-xl-table-cell">
                          {formatNumber(company.paidUsers)}
                        </td>
                        <td className="text-end align-middle">
                          <strong>{formatNumber(company.totalUsers)}</strong>
                        </td>
                        <td className="text-center align-middle d-none d-lg-table-cell">
                          {company.usesStripe == null ? (
                            <span className="text-muted">{EMPTY}</span>
                          ) : (
                            <span className={`badge ${company.usesStripe ? 'bg-success' : 'bg-secondary'}`}>
                              {company.usesStripe ? 'Stripe' : 'Manual'}
                            </span>
                          )}
                        </td>
                        <td className="text-center align-middle d-none d-lg-table-cell">
                          {formatBillingCycle(company.paymentFrequency)}
                        </td>
                        <td className="text-center align-middle">
                          {company.accountType ? (
                            <span className="badge bg-dark">{planLabel(company.accountType)}</span>
                          ) : (
                            <span className="text-muted">{EMPTY}</span>
                          )}
                        </td>
                        <td className="text-center align-middle text-nowrap d-none d-md-table-cell">
                          {formatDate(company.renewalDate)}
                        </td>
                        {!isCrmView && (
                          <td className="text-center align-middle text-nowrap d-none d-md-table-cell">
                            {canEdit && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleJoinCompany(company)}
                                disabled={joiningCompanyId === company.id}
                              >
                                {joiningCompanyId === company.id ? (
                                  <>
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                      className="me-1"
                                    />
                                    Joining...
                                  </>
                                ) : (
                                  'Join company'
                                )}
                              </Button>
                            )}
                            {joinResult?.companyId === company.id && (
                              <div className={`save-status mt-1 ${joinResult.ok ? 'is-success' : 'is-error'}`} role="status">
                                {joinResult.message}
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
            {!error && renderPagination()}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Companies;
